import React, { Component, Suspense, useEffect } from 'react'
import { HashRouter, Route, Routes } from 'react-router-dom'
import axios from './components/utilities/axios'
import './scss/style.scss'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"/>
  </div>
)

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))

const SESSION_LIMIT_MS = 2 * 60 * 60 * 1000; // 2 hours
const CHECK_INTERVAL_MS = 2 * 60 * 60 * 1000; // re-check every 2 hours

// Normalizes 'YYYY-MM-DD HH:mm:ss' (backend format) so browsers parse it as
// a valid local date (Safari rejects the space-separated form).
const parseServerDate = (value) => {
  const s = String(value || '').trim();
  if (!s) return 0;
  const ms = new Date(s).getTime();
  if (ms) return ms;
  return new Date(s.replace(' ', 'T')).getTime();
};

const SESSION_KEYS = [
  'y-z-main',
  'userId',
  'userData',
  'permit',
  'sessionLoginTime'
];

// Logs out sessions older than 2 hours: marks the server session logged out,
// clears the stored auth variables and refreshes the app so the latest UI
// build is pulled on next load.
const SessionGuard = () => {
  useEffect(() => {
    const clearStorage = () => {
      SESSION_KEYS.forEach((key) => window.localStorage.removeItem(key));
      window.sessionStorage.clear();
    };

    const forceLogout = () => {
      // best-effort: mark the server session as logged out (ignores failures)
      try {
        const userData = JSON.parse(
          window.localStorage.getItem('userData') || '{}'
        );
        if (userData.userId) {
          axios
            .put(`/employees/insertLogout`, { employeeId: userData.userId })
            .catch(() => {});
        }
      } catch (e) {
        /* ignore */
      }
      clearStorage();
      window.location.reload();
    };

    // New logins store sessionLoginTime locally; older sessions don't, so we
    // fall back to the authoritative last-login time from login_activities.
    const checkSession = async () => {
      const isLoggedIn = !!window.localStorage.getItem('userData');
      if (!isLoggedIn) {
        return;
      }

      let loginTime = parseInt(
        window.localStorage.getItem('sessionLoginTime') || '0',
        10
      );

      if (!loginTime) {
        try {
          const userData = JSON.parse(
            window.localStorage.getItem('userData') || '{}'
          );
          const { data } = await axios.get(`/employees/login-session-time`, {
            params: { employeeId: userData.userId }
          });
          if (data.success && data.loginDateTime) {
            loginTime = parseServerDate(data.loginDateTime);
            if (loginTime) {
              window.localStorage.setItem(
                'sessionLoginTime',
                String(loginTime)
              );
              if (Date.now() - loginTime < SESSION_LIMIT_MS) {
                return; // fresh session, leave them in
              }
            }
          }
        } catch (e) {
          // Unknown session age and backend unreachable: be conservative and
          // keep the user logged in rather than force a logout on error.
          return;
        }
      }

      if (loginTime && Date.now() - loginTime >= SESSION_LIMIT_MS) {
        forceLogout();
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  return null;
};

class App extends Component {
  render() {
    return (
      <HashRouter>
        <SessionGuard />
        <Suspense fallback={loading}>
          <Routes>
            <Route exact path="/login" name="Login Page" element={<Login />} />
            <Route exact path="/register" name="Register Page" element={<Register />} />
            <Route exact path="/404" name="Page 404" element={<Page404 />} />
            <Route exact path="/500" name="Page 500" element={<Page500 />} />
            <Route path="*" name="Home" element={<DefaultLayout />} />
          </Routes>
        </Suspense>
      </HashRouter>
    )
  }
}

export default App
